<template>
    <basic-header pretitle="Aqfer graph management" title="Graph Curation" />

    <div class="container-fluid graph-curation-page mt-3 mb-4">
        <!-- ── Unified Topbar ──────────────────────────────────────────── -->
        <div class="card border-0 shadow-sm curation-topbar-card">
            <div class="card-body curation-topbar-body">
                <div class="curation-topbar-inner">
                    <!-- Controls: Dataset | Records | Range | Load -->
                    <div class="d-flex align-items-center gap-2 gap-lg-3 min-w-0 curation-controls-group">
                        <!-- Dataset -->
                        <div class="d-flex align-items-center gap-2 flex-shrink-0">
                            <span class="curation-inline-label">Dataset</span>
                            <div class="btn-group btn-group-sm dataset-toggle" role="group">
                                <button
                                    type="button"
                                    class="btn"
                                    :class="dataset === 'merge' ? 'btn-primary' : 'btn-outline-primary'"
                                    @click="setDataset('merge')"
                                >Merge</button>
                                <button
                                    type="button"
                                    class="btn"
                                    :class="dataset === 'split' ? 'btn-primary' : 'btn-outline-primary'"
                                    @click="setDataset('split')"
                                >Split</button>
                            </div>
                        </div>

                        <div class="curation-v-sep"></div>

                        <!-- Records -->
                        <div class="d-flex align-items-center gap-2 flex-shrink-0">
                            <span class="curation-inline-label">Records</span>
                            <input
                                v-model.number="recordCountInput"
                                type="number"
                                class="form-control form-control-sm curation-records-input"
                                min="1"
                                step="1"
                                title="Number of recommendations to load (from current offset)"
                            />
                        </div>

                        <!-- Range -->
                        <div class="d-flex align-items-center gap-2 flex-shrink-0">
                            <span class="curation-inline-label">Range</span>
                            <span v-if="offsetLoading" class="spinner-border spinner-border-sm text-secondary" role="status" aria-label="Loading offset…" />
                            <span v-else-if="loadRangeLabel" class="font-monospace small fw-semibold curation-range-value">{{ loadRangeLabel }}</span>
                            <span v-else class="small text-muted">—</span>
                        </div>

                        <!-- Load button -->
                        <div class="ms-auto flex-shrink-0">
                            <button
                                type="button"
                                class="btn btn-primary btn-sm px-2 px-lg-3 text-nowrap"
                                :disabled="listLoading || !canLoadList"
                                title="Fetches the current offset from the API, then loads this many recommendations starting at that offset"
                                @click="loadRecommendations"
                            >
                                <span v-if="listLoading" class="spinner-border spinner-border-sm me-1" role="status" />
                                <i v-else class="fa fa-list me-1" />
                                <span class="d-none d-md-inline">Load recommendations</span><span class="d-md-none">Load</span>
                            </button>
                        </div>
                    </div>

                    <!-- Vertical separator between controls and stats (xl+) -->
                    <div class="curation-topbar-sep"></div>

                    <!-- Stats + Submit -->
                    <div class="d-flex align-items-center gap-2 gap-lg-3 flex-shrink-0 curation-stats-group">
                        <div class="curation-stat-chip" title="Rows returned from the last load">
                            <span class="curation-stat-chip__label">Rows loaded</span>
                            <span class="curation-stat-chip__value text-primary">{{ recommendations.length }}</span>
                        </div>
                        <div class="curation-v-sep"></div>
                        <div
                            class="curation-stat-chip"
                            title="Every loaded row needs Accept or Reject before you can submit (partial batches are not allowed)."
                        >
                            <span class="curation-stat-chip__label">Decisions</span>
                            <span class="curation-stat-chip__value curation-stat-chip__value--decisions">{{ decisionsProgressLabel }}</span>
                        </div>
                        <button
                            type="button"
                            class="btn btn-success btn-sm btn-submit-review text-nowrap"
                            :disabled="submitting || !allDecisionsComplete"
                            :title="
                                allDecisionsComplete
                                    ? 'Save decisions to the graph service'
                                    : 'Choose Accept or Reject for every recommendation in the list before saving'
                            "
                            @click="submitDecisions"
                        >
                            <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status" />
                            <i v-else class="fa fa-cloud-arrow-up me-1" />
                            Save Decisions
                        </button>
                    </div>
                </div>
                <div v-if="offsetError" class="alert alert-warning mt-2 mb-0 py-2 small">{{ offsetError }}</div>
                <div
                    v-else-if="currentOffset != null && (!Number.isFinite(Number(recordCountInput)) || Number(recordCountInput) < 1)"
                    class="alert alert-info mt-2 mb-0 py-2 small"
                >
                    Enter at least 1 record to load (default 10).
                </div>
            </div>
        </div>

        <div class="row g-2 mb-0 align-items-stretch curation-bench-row">
            <div class="col-12 col-lg-4 curation-bench-col d-flex">
                <div class="card border-0 shadow-sm recommendations-card w-100 d-flex flex-column">
                    <div
                        class="card-header bg-transparent border-0 d-flex align-items-center justify-content-between recommendations-card-header"
                    >
                        <h5 class="card-title mb-0">Recommendations</h5>
                        <span v-if="listPartial" class="badge bg-warning text-dark">Partial result</span>
                    </div>
                    <div class="card-body d-flex flex-column flex-grow-1 curation-list-body">
                        <div v-if="listError" class="alert alert-danger small">{{ listError }}</div>
                        <div v-else-if="listLoading && !recommendations.length" class="text-center py-5">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading recommendations…</span>
                            </div>
                            <p class="text-muted small mt-2 mb-0">Loading recommendations…</p>
                        </div>
                        <div v-else-if="!recommendations.length" class="text-muted text-center py-5">
                            <i class="fa fa-diagram-project fa-2x mb-3 d-block opacity-50" />
                            Click
                            <strong>Load recommendations</strong> to fetch rows.
                        </div>
                        <template v-else>
                            <div class="d-flex flex-wrap align-items-center gap-2 mb-2 curation-bulk-bar">
                                <span class="small text-muted me-auto selected-count">{{ selectionCount }} selected</span>
                                <button
                                    type="button"
                                    class="btn btn-outline-success btn-sm"
                                    :disabled="!selectionCount"
                                    @click="bulkSetDecision('accept')"
                                >
                                    Accept selected
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-outline-danger btn-sm"
                                    :disabled="!selectionCount"
                                    @click="bulkSetDecision('reject')"
                                >
                                    Reject selected
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-link btn-sm text-secondary py-0"
                                    :disabled="!selectionCount"
                                    @click="clearRecommendationSelection"
                                >
                                    Clear selection
                                </button>
                            </div>
                            <div class="curation-table-wrap">
                            <table class="table table-hover table-sm align-middle mb-0 curation-data-table">
                                <colgroup v-if="dataset === 'merge'">
                                    <col class="curation-col-w-check" />
                                    <col class="curation-col-w-recid" />
                                    <col />
                                    <col />
                                    <col class="curation-col-w-actions" />
                                </colgroup>
                                <colgroup v-else>
                                    <col class="curation-col-w-check" />
                                    <col class="curation-col-w-recid" />
                                    <col />
                                    <col class="curation-col-w-splitinto" />
                                    <col class="curation-col-w-actions" />
                                </colgroup>
                                <thead class="table-light">
                                    <tr>
                                        <th scope="col" class="curation-th-checkbox text-center">
                                            <input
                                                ref="selectAllCheckbox"
                                                type="checkbox"
                                                class="form-check-input m-0"
                                                title="Select all rows"
                                                aria-label="Select all rows"
                                                @change="onSelectAllChange"
                                            />
                                        </th>
                                        <th scope="col" class="text-muted small text-uppercase curation-col-recid">Rec ID</th>
                                        <template v-if="dataset === 'merge'">
                                            <th scope="col" class="text-muted small text-uppercase curation-col-cluster">Cluster 1</th>
                                            <th scope="col" class="text-muted small text-uppercase curation-col-cluster">Cluster 2</th>
                                        </template>
                                        <template v-else>
                                            <th scope="col" class="text-muted small text-uppercase curation-col-cluster">Cluster</th>
                                            <th scope="col" class="text-muted small text-uppercase curation-col-splitinto">Split into</th>
                                        </template>
                                        <th scope="col" class="text-muted small text-uppercase curation-col-actions-h">Decision</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="(row, idx) in recommendations"
                                        :key="rowKey(row, idx)"
                                        :class="{ 'table-active': selectedRowIndex === idx }"
                                        class="curation-row"
                                        @click="selectRow(row, idx)"
                                    >
                                        <td class="text-center curation-td-checkbox" @click.stop>
                                            <input
                                                type="checkbox"
                                                class="form-check-input m-0"
                                                :checked="isRowBulkChecked(row, idx)"
                                                :aria-label="'Select row ' + (idx + 1)"
                                                @change="setRowBulkChecked(row, idx, $event.target.checked)"
                                            />
                                        </td>
                                        <td class="font-monospace small curation-col-recid">{{ row.recommendation_id ?? '—' }}</td>
                                        <template v-if="dataset === 'merge'">
                                            <td class="font-monospace small curation-col-cluster">
                                                <button
                                                    type="button"
                                                    class="cluster-link-btn cluster-link-btn--cell"
                                                    :class="{ 'is-anchor-active': selectedRowIndex === idx && mergeAnchorHighlightsC1 }"
                                                    :title="row.cluster_id1"
                                                    @click.stop="selectRowAndFocus(row, idx, 'c1')"
                                                >
                                                    {{ row.cluster_id1 }}
                                                </button>
                                            </td>
                                            <td class="font-monospace small curation-col-cluster">
                                                <button
                                                    type="button"
                                                    class="cluster-link-btn cluster-link-btn--cell"
                                                    :class="{ 'is-anchor-active': selectedRowIndex === idx && mergeAnchorHighlightsC2 }"
                                                    :title="row.cluster_id2"
                                                    @click.stop="selectRowAndFocus(row, idx, 'c2')"
                                                >
                                                    {{ row.cluster_id2 }}
                                                </button>
                                            </td>
                                        </template>
                                        <template v-else>
                                            <td class="font-monospace small curation-col-cluster">
                                                <button
                                                    type="button"
                                                    class="cluster-link-btn cluster-link-btn--cell"
                                                    :class="{ 'is-anchor-active': selectedRowIndex === idx }"
                                                    :title="splitRowClusterId(row)"
                                                    @click.stop="selectRowAndFocus(row, idx, 'c1')"
                                                >
                                                    {{ splitRowClusterId(row) }}
                                                </button>
                                            </td>
                                            <td class="curation-col-splitinto" @click.stop>
                                                <input
                                                    v-model.number="row.split_into"
                                                    type="number"
                                                    class="form-control form-control-sm curation-splitinto-input"
                                                    min="2"
                                                    max="99"
                                                    step="1"
                                                    title="Number of clusters this split should produce (minimum 2)"
                                                    @blur="normalizeSplitInto(row)"
                                                />
                                            </td>
                                        </template>
                                        <td class="curation-col-actions" @click.stop>
                                            <div class="curation-action-btns d-flex gap-1 align-items-center justify-content-center">
                                                <button
                                                    type="button"
                                                    class="btn btn-sm curation-decision-btn"
                                                    :class="decisionClass(row, idx, 'accept')"
                                                    title="Accept"
                                                    aria-label="Accept"
                                                    @click="setDecision(row, idx, 'accept')"
                                                >
                                                    <i class="fa fa-check" />
                                                </button>
                                                <button
                                                    type="button"
                                                    class="btn btn-sm curation-decision-btn"
                                                    :class="decisionClass(row, idx, 'reject')"
                                                    title="Reject"
                                                    aria-label="Reject"
                                                    @click="setDecision(row, idx, 'reject')"
                                                >
                                                    <i class="fa fa-xmark" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            </div>
                        </template>
                    </div>
                </div>
            </div>

            <div class="col-12 col-lg-8 curation-bench-col d-flex">
                <div class="card border-0 shadow-sm graph-detail-card w-100 d-flex flex-column">
                    <div
                        class="card-header bg-transparent border-0 d-flex flex-wrap align-items-center gap-2 graph-detail-card-header"
                    >
                        <h5 class="card-title mb-0 me-auto">Cluster graph</h5>
                        <template v-if="dataset === 'merge' && selectedRow">
                            <div class="d-flex align-items-center gap-2 flex-shrink-0 flex-wrap">
                            <div class="cluster-anchor-row cluster-anchor-row--header btn-group btn-group-sm" role="group" aria-label="Cluster scope">
                                <button
                                    type="button"
                                    class="btn btn-anchor"
                                    :class="detailFocus === 'both' ? 'btn-anchor-active' : 'btn-anchor-idle'"
                                    @click="setDetailFocus('both')"
                                >
                                    Both
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-anchor btn-anchor-c1"
                                    :class="detailFocus === 'c1' ? 'btn-anchor-active' : 'btn-anchor-idle'"
                                    :title="selectedRow ? (selectedRow.cluster_id1 || selectedRow.cluster_id) : 'Cluster 1'"
                                    @click="setDetailFocus('c1')"
                                >
                                    Cluster 1
                                    <span v-if="selectedRow" class="cluster-btn-id font-monospace">{{ shortClusterId(selectedRow.cluster_id1 || selectedRow.cluster_id) }}</span>
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-anchor btn-anchor-c2"
                                    :class="detailFocus === 'c2' ? 'btn-anchor-active' : 'btn-anchor-idle'"
                                    :title="selectedRow?.cluster_id2 || 'Cluster 2'"
                                    @click="setDetailFocus('c2')"
                                >
                                    Cluster 2
                                    <span v-if="selectedRow?.cluster_id2" class="cluster-btn-id font-monospace">{{ shortClusterId(selectedRow.cluster_id2) }}</span>
                                </button>
                            </div>
                            <div class="form-check form-check-inline mb-0 small">
                                <input
                                    id="curation-merge-preview"
                                    v-model="mergePreviewEnabled"
                                    class="form-check-input"
                                    type="checkbox"
                                />
                                <label class="form-check-label text-muted" for="curation-merge-preview">Preview merged</label>
                            </div>
                            </div>
                        </template>
                    </div>
                    <div class="card-body graph-detail-card-body flex-grow-1 d-flex flex-column">
                        <div v-if="detailError" class="alert alert-danger small">{{ detailError }}</div>
                        <div v-else-if="detailLoading" class="text-center py-5">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading graph…</span>
                            </div>
                        </div>
                        <div v-else-if="!detailGraph && !selectedRow" class="text-muted text-center py-5">
                            <i class="fa fa-circle-nodes fa-2x mb-3 d-block opacity-50" />
                            Select a row from the recommendations list to view its cluster graph.
                        </div>
                        <div v-else-if="!detailGraph" class="text-muted text-center py-5">
                            <i class="fa fa-triangle-exclamation fa-2x mb-3 d-block opacity-50" />
                            No graph data available for this recommendation.
                        </div>
                        <div v-else class="graph-detail-inner">
                            <div
                                v-if="detailGraph"
                                class="cluster-graph-id-filter mb-2 d-flex flex-wrap align-items-center gap-2"
                            >
                                <label class="small text-muted mb-0" for="curation-graph-id-filter">Filter by ID</label>
                                <input
                                    id="curation-graph-id-filter"
                                    v-model.trim="curationGraphIdFilterInput"
                                    type="text"
                                    class="form-control form-control-sm curation-graph-id-filter-input"
                                    placeholder="Substring matches entity ids in both clusters"
                                    autocomplete="off"
                                    @blur="flushCurationIdFilter"
                                    @keydown.enter.prevent="flushCurationIdFilter"
                                />
                            </div>
                            <div
                                v-if="canLoadMoreOntoGraph"
                                class="cluster-graph-pager mb-2 d-flex flex-wrap align-items-center gap-2"
                            >
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-primary"
                                    @click="loadMoreGraphEntitiesOnGraph"
                                >
                                    Load next {{ graphEntityStep }} entities on graph
                                </button>
                                <span class="small text-muted">
                                    Showing {{ entityGraphCap }} per hub with flat lists (max {{ graphEntityCapMax }}).
                                    Neighbor-style lists follow the group-by setting above.
                                </span>
                            </div>
                            <D3CurationGraph
                                :data="detailGraphFilteredForDisplay"
                                :node-colors="graphNodeColors"
                                :node-labels="graphNodeLabels"
                                :show-bridge-stats="dataset === 'merge' && !mergePreviewEnabled"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import BasicHeader from '../../components/BasicHeader.vue'
import D3CurationGraph from '../../components/D3CurationGraph.vue'

const MAX_POLL = 60
const POLL_MS = 1000
const DEFAULT_SPLIT_INTO = 2
/** Flat entity_list*: initial dots per hub; user can raise with “Load next on graph” up to CURATION_GRAPH_ENTITY_CAP_MAX. */
const CURATION_GRAPH_FLAT_ENTITY_CAP = 80
const CURATION_GRAPH_ENTITY_STEP = 80
const CURATION_GRAPH_ENTITY_CAP_MAX = 400
const CURATION_BUNDLE_ID_PREVIEW = 250

export default {
    name: 'GraphCuration',
    components: { BasicHeader, D3CurationGraph },
    data() {
        return {
            dataset: 'merge',
            configTableName: null,
            configLoading: false,
            configError: null,
            /** Next recommendation_id cursor from graph curation offset API. */
            currentOffset: null,
            recordCountInput: 10,
            offsetLoading: false,
            offsetError: null,
            listLoading: false,
            listError: null,
            listPartial: false,
            recommendations: [],
            selectedRowIndex: null,
            selectedRow: null,
            detailLoading: false,
            detailError: null,
            /** Bumped on every new detail request/reset; stale async responses check this before committing. */
            detailRequestSeq: 0,
            detailGraph: null,
            /** Clone of `detailGraph` for filtering / preview (stable reference). */
            detailGraphMerged: null,
            detailCacheHit: false,
            detailCache: Object.create(null),
            /** Merge: 'both' | 'c1' | 'c2' (visual only; graph always shows both hubs). Split: 'c1'. */
            detailFocus: 'both',
            mergePreviewEnabled: false,
            /** Per-hub cap for flat comma-separated entity lists (neighbor-group strings ignore this). */
            entityGraphCap: CURATION_GRAPH_FLAT_ENTITY_CAP,
            /** Applied to graph filter (debounced from typing). */
            curationGraphIdFilter: '',
            /** Bound to the ID filter input; updates `curationGraphIdFilter` after a short delay. */
            curationGraphIdFilterInput: '',
            decisions: Object.create(null),
            recommendationSelection: Object.create(null),
            submitting: false,
            graphNodeColors: {
                cluster: '#6f42c1',
                entity: '#0d6efd',
                entity_bundle: '#6610f2',
            },
            graphNodeLabels: {
                cluster: 'Cluster',
                entity: 'Entity',
                entity_group: 'Type~domain',
                entity_bundle: 'Collapsed entities',
            },
        }
    },
    computed: {
        cid() {
            return this.$store.state.currentClient?.cid
        },
        canLoadList() {
            if (!this.cid) return false
            if (!this.configTableName) return false
            if (this.currentOffset == null) return false
            const n = Number(this.recordCountInput)
            return Number.isFinite(n) && n >= 1
        },
        endOffset() {
            const start = Math.max(1, Number(this.currentOffset))
            if (!Number.isFinite(start)) return null
            const n = Math.max(1, Math.floor(Number(this.recordCountInput)) || 10)
            return start + n - 1
        },
        loadRangeLabel() {
            if (this.currentOffset == null || this.offsetError) return null
            const start = Math.max(1, Number(this.currentOffset))
            if (!Number.isFinite(start)) return null
            const n = Number(this.recordCountInput)
            if (!Number.isFinite(n) || n < 1) return String(start)
            const end = start + Math.floor(n) - 1
            return `${start} – ${end}`
        },
        /** Stable cache key for detail rows (recommendation-scoped). */
        activeDetailCacheKey() {
            const rid = this.activeDetailRecommendationId
            return rid != null ? this.cacheKeyForRecommendation(rid) : null
        },
        activeDetailRecommendationId() {
            if (!this.selectedRow) return null
            const r = this.selectedRow.recommendation_id
            if (r == null || String(r).trim() === '') return null
            return String(r).trim()
        },
        mergeAnchorHighlightsC1() {
            if (this.dataset !== 'merge') return false
            return this.detailFocus === 'both' || this.detailFocus === 'c1'
        },
        mergeAnchorHighlightsC2() {
            if (this.dataset !== 'merge') return false
            return this.detailFocus === 'both' || this.detailFocus === 'c2'
        },
        /** Merge graph scoped by Cluster 1 / 2 / Both (preview graph ignores focus). */
        curationGraphAfterFocus() {
            return this.applyDetailFocusToMergeGraph(this.curationGraphUnfiltered)
        },
        /** Raw graph before ID filter: live detail or merged preview (merge only). */
        curationGraphUnfiltered() {
            if (this.dataset === 'merge' && this.mergePreviewEnabled && this.activeDetailCacheKey) {
                const entry = this.detailCache[this.activeDetailCacheKey]
                if (entry && entry.rows && entry.rows.length) {
                    const effCap = this.effectiveFlatCapForRows(entry.rows)
                    const g = this.buildMergePreviewGraph(entry.rows, effCap)
                    if (g && g.nodes && g.nodes.length) return g
                }
            }
            return this.detailGraphMerged
        },
        detailGraphFilteredForDisplay() {
            const base = this.curationGraphAfterFocus
            if (!base || !base.nodes || !base.edges) return base
            const q = String(this.curationGraphIdFilter || '')
                .trim()
                .toLowerCase()
            if (!q) return base
            const nodeMatches = (n) => {
                if (n.type === 'entity') return String(n.key || '').toLowerCase().includes(q)
                if (n.type === 'entity_group') {
                    if (String(n.neighborType || n.key || '').toLowerCase().includes(q)) return true
                    const ids = n.entityIds && n.entityIds.length ? n.entityIds : []
                    if (ids.some((id) => String(id).toLowerCase().includes(q))) return true
                    const subs = n.entitySubgroups || []
                    for (let i = 0; i < subs.length; i++) {
                        const row = subs[i].entityIds || []
                        if (row.some((id) => String(id).toLowerCase().includes(q))) return true
                    }
                    return false
                }
                if (n.type === 'entity_bundle') {
                    const prev = n.bundleHiddenPreview || []
                    const all = n.bundleHiddenAllIds || []
                    if (prev.some((id) => String(id).toLowerCase().includes(q))) return true
                    if (all.some((id) => String(id).toLowerCase().includes(q))) return true
                    return String(n.key || '').toLowerCase().includes(q)
                }
                return false
            }
            const keep = new Set()
            for (let i = 0; i < base.nodes.length; i++) {
                const n = base.nodes[i]
                if (n.type === 'cluster') keep.add(n.id)
                else if (nodeMatches(n)) keep.add(n.id)
            }
            const nodes = base.nodes.filter((n) => keep.has(n.id))
            const edges = base.edges.filter((e) => keep.has(e.source) && keep.has(e.target))
            return { nodes, edges }
        },
        decisionCount() {
            return Object.keys(this.decisions).length
        },
        decisionsProgressLabel() {
            const n = this.recommendations.length
            if (!n) return '—'
            return `${this.decisionCount} / ${n}`
        },
        /** Submit only when every loaded row has accept or reject (no partial batches). */
        allDecisionsComplete() {
            const rows = this.recommendations
            const n = rows.length
            if (!n) return false
            for (let i = 0; i < n; i++) {
                const d = this.decisions[this.rowKey(rows[i], i)]
                if (d !== 'accept' && d !== 'reject') return false
                if (this.dataset === 'split') {
                    const si = Number(rows[i].split_into)
                    if (!Number.isFinite(si) || si < 2) return false
                }
            }
            return true
        },
        selectionCount() {
            return Object.keys(this.recommendationSelection).length
        },
        recommendationsTable() {
            return this.configTableName
        },
        graphEntityStep() {
            return CURATION_GRAPH_ENTITY_STEP
        },
        graphEntityCapMax() {
            return CURATION_GRAPH_ENTITY_CAP_MAX
        },
        /** Largest flat (non–neighbor-group) entity list in cached detail rows for the active cluster. */
        maxFlatEntityCountInCachedDetail() {
            const key = this.activeDetailCacheKey
            if (!key) return 0
            const entry = this.detailCache[key]
            if (!entry || !entry.rows || !entry.rows.length) return 0
            return this.maxFlatEntityCountForRows(entry.rows)
        },
        canLoadMoreOntoGraph() {
            if (!this.detailGraph || this.mergePreviewEnabled) return false
            const maxR = this.maxFlatEntityCountInCachedDetail
            if (maxR <= CURATION_GRAPH_FLAT_ENTITY_CAP) return false
            return this.entityGraphCap < Math.min(maxR, CURATION_GRAPH_ENTITY_CAP_MAX)
        },
    },
    watch: {
        cid: {
            immediate: true,
            async handler() {
                this.resetCurationScope()
                if (this.cid) {
                    await Promise.all([this.refreshOffset(), this.fetchCurationConfig()])
                }
            },
        },
        recommendations: {
            handler() {
                this.$nextTick(() => this.syncSelectAllCheckbox())
            },
        },
        recommendationSelection: {
            deep: true,
            handler() {
                this.$nextTick(() => this.syncSelectAllCheckbox())
            },
        },
        curationGraphIdFilterInput: {
            handler() {
                this.scheduleCurationIdFilterApply()
            },
        },
    },
    beforeUnmount() {
        if (this._curationIdFilterTimer) {
            clearTimeout(this._curationIdFilterTimer)
            this._curationIdFilterTimer = null
        }
    },
    methods: {
        scheduleCurationIdFilterApply() {
            if (this._curationIdFilterTimer) {
                clearTimeout(this._curationIdFilterTimer)
                this._curationIdFilterTimer = null
            }
            this._curationIdFilterTimer = setTimeout(() => {
                this._curationIdFilterTimer = null
                this.curationGraphIdFilter = String(this.curationGraphIdFilterInput || '').trim()
            }, 400)
        },
        flushCurationIdFilter() {
            if (this._curationIdFilterTimer) {
                clearTimeout(this._curationIdFilterTimer)
                this._curationIdFilterTimer = null
            }
            this.curationGraphIdFilter = String(this.curationGraphIdFilterInput || '').trim()
        },
        rowKey(row, idx) {
            if (row.recommendation_id != null && row.recommendation_id !== '') {
                return `rec:${row.recommendation_id}`
            }
            if (this.dataset === 'split') {
                const cid = row.cluster_id ?? row.cluster_id1
                return `split:${cid}::${idx}`
            }
            return `${row.cluster_id1}::${row.cluster_id2}::${idx}`
        },
        async onDatasetChange() {
            this.resetCurationScope()
            if (this.cid) {
                await Promise.all([this.refreshOffset(), this.fetchCurationConfig()])
            }
        },
        setDataset(value) {
            if (this.dataset === value) return
            this.dataset = value
            this.onDatasetChange()
        },
        resetListState() {
            this.detailRequestSeq += 1
            this.recommendations = []
            this.listError = null
            this.listPartial = false
            this.selectedRow = null
            this.selectedRowIndex = null
            this.detailGraph = null
            this.detailGraphMerged = null
            this.detailError = null
            this.detailLoading = false
            this.detailCacheHit = false
            this.detailFocus = this.dataset === 'merge' ? 'both' : 'c1'
            this.detailCache = Object.create(null)
            this.entityGraphCap = CURATION_GRAPH_FLAT_ENTITY_CAP
            this.curationGraphIdFilter = ''
            this.curationGraphIdFilterInput = ''
            if (this._curationIdFilterTimer) {
                clearTimeout(this._curationIdFilterTimer)
                this._curationIdFilterTimer = null
            }
            this.mergePreviewEnabled = false
            this.decisions = Object.create(null)
            this.recommendationSelection = Object.create(null)
        },
        resetCurationScope() {
            this.resetListState()
            this.currentOffset = null
            this.recordCountInput = 10
            this.offsetError = null
            this.offsetLoading = false
            this.configTableName = null
            this.configError = null
            this.configLoading = false
        },
        cacheKeyForRecommendation(recommendationId) {
            return `${this.dataset}::rec::${String(recommendationId)}`
        },
        sqlRecommendationWhereEquals(value) {
            const s = String(value).trim()
            if (!s) return 'recommendation_id IS NULL'
            if (/^-?\d+(\.\d+)?$/.test(s) && Number.isFinite(Number(s))) {
                return `recommendation_id = ${Number(s)}`
            }
            return `recommendation_id = '${this.sqlLiteral(s)}'`
        },
        decisionClass(row, idx, action) {
            const key = this.rowKey(row, idx)
            const d = this.decisions[key]
            if (d === action) {
                return action === 'accept' ? 'btn-success' : 'btn-danger'
            }
            return action === 'accept' ? 'btn-outline-success' : 'btn-outline-danger'
        },
        setDecision(row, idx, action) {
            const key = this.rowKey(row, idx)
            const next = { ...this.decisions }
            if (next[key] === action) delete next[key]
            else next[key] = action
            this.decisions = next
        },
        isRowBulkChecked(row, idx) {
            return !!this.recommendationSelection[this.rowKey(row, idx)]
        },
        setRowBulkChecked(row, idx, checked) {
            const k = this.rowKey(row, idx)
            const next = { ...this.recommendationSelection }
            if (checked) next[k] = true
            else delete next[k]
            this.recommendationSelection = next
        },
        clearRecommendationSelection() {
            this.recommendationSelection = Object.create(null)
            this.$nextTick(() => this.syncSelectAllCheckbox())
        },
        bulkSetDecision(action) {
            if (!this.selectionCount) return
            const next = { ...this.decisions }
            this.recommendations.forEach((row, idx) => {
                const k = this.rowKey(row, idx)
                if (!this.recommendationSelection[k]) return
                next[k] = action
            })
            this.decisions = next
        },
        onSelectAllChange(e) {
            const checked = e.target.checked
            if (checked) {
                const next = { ...this.recommendationSelection }
                this.recommendations.forEach((r, i) => {
                    next[this.rowKey(r, i)] = true
                })
                this.recommendationSelection = next
            } else {
                this.recommendationSelection = Object.create(null)
            }
            this.$nextTick(() => this.syncSelectAllCheckbox())
        },
        syncSelectAllCheckbox() {
            const el = this.$refs.selectAllCheckbox
            if (!el) return
            const n = this.recommendations.length
            const c = this.selectionCount
            if (!n) {
                el.checked = false
                el.indeterminate = false
                return
            }
            if (c === 0) {
                el.checked = false
                el.indeterminate = false
            } else if (c === n) {
                el.checked = true
                el.indeterminate = false
            } else {
                el.checked = false
                el.indeterminate = true
            }
        },
        async refreshOffset() {
            if (!this.cid) return
            this.offsetLoading = true
            this.offsetError = null
            try {
                const base = this.$store.state.graphServiceUrl
                const res = await axios.get(
                    `${base}/v1/cids/${encodeURIComponent(this.cid)}/curation/offset`,
                    {
                        params: { dataset: this.dataset },
                        headers: { Authorization: `Bearer ${this.$store.state.accessToken}` },
                    }
                )
                const cur = res?.data?.current_offset
                if (cur == null || !Number.isFinite(Number(cur))) {
                    this.offsetError = 'No recommendations found.'
                    this.currentOffset = null
                    return
                }
                this.currentOffset = Number(cur)
                const rc = Number(this.recordCountInput)
                if (!Number.isFinite(rc) || rc < 1) {
                    this.recordCountInput = 10
                }
            } catch (e) {
                this.currentOffset = null
                if (e.response?.status === 404) {
                    this.offsetError = 'No recommendations found.'
                } else {
                    this.offsetError =
                        e.response?.data?.message ||
                        e.response?.data?.error ||
                        e.message ||
                        'No recommendations found.'
                }
            } finally {
                this.offsetLoading = false
            }
        },
        async fetchCurationConfig() {
            if (!this.cid) return
            this.configLoading = true
            this.configError = null
            try {
                const base = this.$store.state.graphServiceUrl
                const res = await axios.get(
                    `${base}/v1/cids/${encodeURIComponent(this.cid)}/curation/config`,
                    {
                        params: { dataset: this.dataset },
                        headers: { Authorization: `Bearer ${this.$store.state.accessToken}` },
                    }
                )
                this.configTableName = res?.data?.recommendation_table_name || null
                if (!this.configTableName) {
                    this.configError = 'No recommendations found.'
                }
            } catch (e) {
                this.configTableName = null
                if (e.response?.status === 404) {
                    this.configError = 'No recommendations found.'
                } else {
                    this.configError =
                        e.response?.data?.message ||
                        e.response?.data?.error ||
                        e.message ||
                        'No recommendations found.'
                }
            } finally {
                this.configLoading = false
            }
        },
        /**
         * PUT /v1/cids/:cid/curation/offset?dataset=… — body is parseOffsetBody JSON (e.g. { current_offset }).
         * Response: { current_offset } (db.CurationOffset).
         */
        async setCurationOffset(toOffsetValue) {
            if (!this.cid) return
            const v = Number(toOffsetValue)
            if (!Number.isFinite(v)) return
            const base = this.$store.state.graphServiceUrl
            const url = `${base}/v1/cids/${encodeURIComponent(this.cid)}/curation/offset`
            const res = await axios.put(
                url,
                { current_offset: v },
                {
                    params: { dataset: this.dataset },
                    headers: {
                        Authorization: `Bearer ${this.$store.state.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            )
            const cur = res.data?.current_offset
            this.currentOffset =
                cur != null && Number.isFinite(Number(cur)) ? Number(cur) : v
        },
        /**
         * Next cursor after this batch: same as list load window end + 1.
         * List uses `recommendation_id between start and start+count-1` → toOffset = start+count-1 → set start to toOffset+1.
         */
        computeNextCurationOffsetAfterSubmit() {
            const start = Math.max(1, Number(this.currentOffset))
            if (!Number.isFinite(start)) return null
            const count = Math.max(1, Math.floor(Number(this.recordCountInput)) || 10)
            const toOffset = start + count - 1
            return toOffset + 1
        },
        /**
         * When merge + Cluster 1/2 focus, show only that hub and its entity edges (linkKind c1/c2).
         * Merge preview (single hub) ignores focus so the graph stays visible.
         */
        applyDetailFocusToMergeGraph(graph) {
            if (!graph || !graph.nodes || !graph.edges) return graph
            if (this.dataset !== 'merge' || this.mergePreviewEnabled) return graph
            const f = this.detailFocus
            if (f === 'both') return graph
            const kindKeep = f === 'c2' ? 'c2' : 'c1'
            const wantIndex = f === 'c2' ? 2 : 1

            /* Primary: find the hub node by clusterIndex (set by buildGraphFromDetailRows
               as 1 or 2). This works for both real API and mock data because it reads
               directly from the built graph instead of matching selectedRow.cluster_id*
               against graph node IDs — those IDs differ on the mock server since the
               list and detail queries return different random cluster IDs. */
            const clusterNodes = graph.nodes.filter((n) => n.type === 'cluster')
            let hubNode = clusterNodes.find((n) => Number(n.clusterIndex) === wantIndex)

            /* Fallback: derive from selectedRow (covers edge cases where clusterIndex
               is absent, e.g. older cached graph shapes). */
            if (!hubNode) {
                const row = this.selectedRow
                if (!row) return graph
                const cid = f === 'c2'
                    ? String(row.cluster_id2 ?? '').trim()
                    : String(row.cluster_id1 ?? row.cluster_id ?? '').trim()
                if (!cid) return graph
                hubNode = graph.nodes.find((n) => n.id === `cluster:${cid}`)
            }

            if (!hubNode) return graph
            const hubKeep = hubNode.id
            const keep = new Set([hubKeep])
            for (let i = 0; i < graph.edges.length; i++) {
                const e = graph.edges[i]
                if ((e.linkKind || '') !== kindKeep) continue
                if (e.source === hubKeep) keep.add(e.target)
                else if (e.target === hubKeep) keep.add(e.source)
            }
            const nodes = graph.nodes.filter((n) => keep.has(n.id))
            const ids = new Set(nodes.map((n) => n.id))
            const edgesOut = graph.edges.filter(
                (e) => ids.has(e.source) && ids.has(e.target) && (e.linkKind || '') === kindKeep
            )
            return { nodes, edges: edgesOut }
        },
        splitRowClusterId(row) {
            const v = row.cluster_id ?? row.cluster_id1
            return v != null && v !== '' ? String(v) : '—'
        },
        /** Abbreviate a cluster ID for the strip: first 8 chars … last 4 chars. */
        shortClusterId(id) {
            if (!id) return '—'
            const s = String(id)
            return s.length > 16 ? `${s.slice(0, 8)}…${s.slice(-4)}` : s
        },
        decorateSplitRow(row) {
            const n = Number(row.split_into)
            return {
                ...row,
                split_into: Number.isFinite(n) && n >= 2 ? Math.min(99, Math.floor(n)) : DEFAULT_SPLIT_INTO,
            }
        },
        normalizeSplitInto(row) {
            let n = Number(row.split_into)
            if (!Number.isFinite(n) || n < 2) n = DEFAULT_SPLIT_INTO
            if (n > 99) n = 99
            row.split_into = Math.floor(n)
        },
        /**
         * @param {{ skipOffsetRefresh?: boolean }} [opts] - If true, caller already refreshed offset; only run the Pail list query.
         */
        async loadRecommendations(opts = {}) {
            const skipOffsetRefresh = opts.skipOffsetRefresh === true
            if (!skipOffsetRefresh && !this.canLoadList) return
            if (
                skipOffsetRefresh &&
                (!this.cid ||
                    this.currentOffset == null ||
                    !Number.isFinite(Number(this.recordCountInput)) ||
                    Number(this.recordCountInput) < 1)
            ) {
                return
            }
            this.listLoading = true
            this.listError = null
            this.listPartial = false
            this.recommendationSelection = Object.create(null)
            this.detailError = null
            this.selectedRow = null
            this.selectedRowIndex = null
            this.detailGraph = null
            this.detailGraphMerged = null
            this.mergePreviewEnabled = false
            this.curationGraphIdFilter = ''
            this.curationGraphIdFilterInput = ''
            if (this._curationIdFilterTimer) {
                clearTimeout(this._curationIdFilterTimer)
                this._curationIdFilterTimer = null
            }
            if (!skipOffsetRefresh) {
                await this.refreshOffset()
            }
            if (this.currentOffset == null) {
                this.listLoading = false
                return
            }
            const start = Math.max(1, Number(this.currentOffset))
            const count = Math.max(1, Math.floor(Number(this.recordCountInput)) || 10)
            const end = start + count - 1
            const tbl = this.recommendationsTable
            const sql =
                this.dataset === 'split'
                    ? `select recommendation_id, cluster_id from ${tbl} where recommendation_id between ${start} and ${end}`
                    : [
                          'select recommendation_id, cluster_id1, cluster_id2',
                          `from ${tbl}`,
                          `where recommendation_id between ${start} and ${end}`,
                      ].join(' ')
            try {
                const raw = await this.runPailQuery(sql)
                this.listPartial = !!raw.partial_result
                const rows = this.rowsFromResult(raw)
                rows.sort((a, b) => {
                    const an = Number(a.recommendation_id)
                    const bn = Number(b.recommendation_id)
                    if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn
                    return String(a.recommendation_id ?? '').localeCompare(String(b.recommendation_id ?? ''))
                })
                this.recommendations = this.dataset === 'split' ? rows.map((r) => this.decorateSplitRow(r)) : rows
                this.decisions = Object.create(null)
            } catch (e) {
                this.recommendations = []
                this.listError = e.message || 'Failed to load recommendations.'
            } finally {
                this.listLoading = false
            }
        },
        selectRow(row, idx) {
            this.detailFocus = this.dataset === 'merge' ? 'both' : 'c1'
            this.entityGraphCap = CURATION_GRAPH_FLAT_ENTITY_CAP
            this.curationGraphIdFilter = ''
            this.curationGraphIdFilterInput = ''
            if (this._curationIdFilterTimer) {
                clearTimeout(this._curationIdFilterTimer)
                this._curationIdFilterTimer = null
            }
            this.mergePreviewEnabled = false
            this.selectedRow = row
            this.selectedRowIndex = idx
            this.loadDetailForRow()
        },
        selectRowAndFocus(row, idx, focus) {
            this.detailFocus =
                this.dataset === 'split' ? 'c1' : focus === 'c2' ? 'c2' : focus === 'both' ? 'both' : 'c1'
            this.entityGraphCap = CURATION_GRAPH_FLAT_ENTITY_CAP
            this.curationGraphIdFilter = ''
            this.curationGraphIdFilterInput = ''
            if (this._curationIdFilterTimer) {
                clearTimeout(this._curationIdFilterTimer)
                this._curationIdFilterTimer = null
            }
            this.mergePreviewEnabled = false
            this.selectedRow = row
            this.selectedRowIndex = idx
            this.loadDetailForRow()
        },
        setDetailFocus(focus) {
            if (!this.selectedRow) return
            if (this.dataset === 'split') {
                this.detailFocus = 'c1'
                return
            }
            const next = focus === 'c2' ? 'c2' : focus === 'c1' ? 'c1' : 'both'
            if (this.detailFocus === next) return
            this.detailFocus = next
        },
        async loadDetailForRow() {
            /* Bump the request generation so a stale response from a row/dataset
               switched away from while this call was in flight is ignored below. */
            this.detailRequestSeq += 1
            const seq = this.detailRequestSeq
            const recId = this.activeDetailRecommendationId
            if (!recId) {
                this.detailGraph = null
                this.syncDetailGraphMerged()
                return
            }
            const cacheKey = this.cacheKeyForRecommendation(recId)
            this.detailError = null
            this.detailCacheHit = !!this.detailCache[cacheKey]
            const cached = this.detailCache[cacheKey]
            if (cached) {
                const rows = cached.rows
                if (rows && rows.length) {
                    const effCap = this.effectiveFlatCapForRows(rows)
                    const graph = this.buildGraphFromDetailRows(rows, effCap)
                    this.detailGraph = graph
                } else {
                    this.detailGraph = cached.graph || cached
                }
                this.syncDetailGraphMerged()
                return
            }
            this.detailLoading = true
            this.detailGraph = null
            this.detailGraphMerged = null
            try {
                const { rows, graph } = await this.fetchClusterGraph(recId)
                if (seq !== this.detailRequestSeq) return
                this.detailGraph = graph
                if (graph && rows && rows.length) {
                    this.detailCache[cacheKey] = { rows, graph }
                } else if (graph) {
                    this.detailCache[cacheKey] = { rows: rows || [], graph }
                }
                this.syncDetailGraphMerged()
            } catch (e) {
                if (seq !== this.detailRequestSeq) return
                this.detailError = e.message || 'Failed to load cluster detail.'
            } finally {
                if (seq === this.detailRequestSeq) this.detailLoading = false
            }
        },
        sqlLiteral(value) {
            return String(value).replace(/'/g, "''")
        },
        async fetchClusterGraph(recommendationId) {
            const wh = this.sqlRecommendationWhereEquals(recommendationId)
            const tbl = this.recommendationsTable
            const sql =
                this.dataset === 'split'
                    ? `select cluster_id, entity_list, score from ${tbl} where ${wh}`
                    : [
                          'select cluster_id1, cluster_id2, entity_list1, entity_list2',
                          `from ${tbl}`,
                          `where ${wh}`,
                      ].join(' ')
            const raw = await this.runPailQuery(sql)
            const rows = this.rowsFromResult(raw)
            const effCap = this.effectiveFlatCapForRows(rows)
            const graph = this.buildGraphFromDetailRows(rows, effCap)
            return { rows, graph }
        },
        maxFlatEntityCountForRows(rows) {
            if (!rows || !rows.length) return 0
            const cols =
                this.dataset === 'split'
                    ? ['entity_list']
                    : ['entity_list1', 'entity_list2', 'entity_list']
            let m = 0
            for (const row of rows) {
                for (const col of cols) {
                    const raw = row[col]
                    if (raw == null || raw === '') continue
                    const norm = this.normalizePailEntityListField(raw)
                    if (norm.includes('entity_neighbor_type')) continue
                    const n = this.parseEntityList(norm).length
                    m = Math.max(m, n)
                }
            }
            return m
        },
        effectiveFlatCapForRows(rows) {
            const maxR = this.maxFlatEntityCountForRows(rows)
            if (maxR < 1) return this.entityGraphCap
            return Math.min(this.entityGraphCap, CURATION_GRAPH_ENTITY_CAP_MAX, maxR)
        },
        loadMoreGraphEntitiesOnGraph() {
            const key = this.activeDetailCacheKey
            if (!key) return
            const entry = this.detailCache[key]
            if (!entry || !entry.rows || !entry.rows.length) return
            const maxR = this.maxFlatEntityCountForRows(entry.rows)
            const next = Math.min(
                this.entityGraphCap + CURATION_GRAPH_ENTITY_STEP,
                CURATION_GRAPH_ENTITY_CAP_MAX,
                maxR
            )
            if (next <= this.entityGraphCap) return
            this.entityGraphCap = next
            const effCap = this.effectiveFlatCapForRows(entry.rows)
            const graph = this.buildGraphFromDetailRows(entry.rows, effCap)
            this.detailGraph = graph
            this.detailCache[key] = { ...this.detailCache[key], graph }
            this.syncDetailGraphMerged()
        },
        /** Clone of `detailGraph` for stable reference (no session composites). */
        syncDetailGraphMerged() {
            const base = this.detailGraph
            if (!base || !base.nodes || !base.edges) {
                this.detailGraphMerged = null
                return
            }
            this.detailGraphMerged = {
                nodes: base.nodes.map((n) => ({ ...n })),
                edges: base.edges.map((e) => ({ ...e })),
            }
        },
        /**
         * Merge preview (merge dataset only): cluster 2 folded into cluster 1 hub — same type~domain or same flat id
         * merges ids; keys only on cluster 2 are flagged mergePreviewNew.
         */
        buildMergePreviewGraph(rows, flatCap = CURATION_GRAPH_FLAT_ENTITY_CAP) {
            const nodes = []
            const edgeKeys = new Set()
            const edges = []
            const pushEdge = (source, target, linkKind) => {
                const ek = `${source}->${target}`
                if (edgeKeys.has(ek)) return
                edgeKeys.add(ek)
                const e = { source, target }
                if (linkKind) e.linkKind = linkKind
                edges.push(e)
            }
            const addNode = (id, type, key, extras = {}) => {
                if (!id || nodes.some((n) => n.id === id)) return
                nodes.push({
                    id,
                    type,
                    key: key || id,
                    ...extras,
                })
            }
            let mainC1 = null
            rows.forEach((row) => {
                const c1 = row.cluster_id1 || row.cluster_id
                if (c1 && !mainC1) mainC1 = c1
            })
            if (!mainC1) return null
            const hubId = `cluster:${mainC1}`
            addNode(hubId, 'cluster', mainC1, { mergePreview: true, hubConnectionCount: 0, clusterIndex: 1 })

            const capFlat = Math.max(1, Math.floor(Number(flatCap)) || CURATION_GRAPH_FLAT_ENTITY_CAP)
            const pairAccum = new Map()
            const ingestPair = (pairLabel, ids, side) => {
                let o = pairAccum.get(pairLabel)
                if (!o) {
                    o = { ids: new Set(), inC1: false, inC2: false }
                    pairAccum.set(pairLabel, o)
                }
                ids.forEach((id) => o.ids.add(id))
                if (side === 'c1') o.inC1 = true
                else o.inC2 = true
            }
            const flatC1 = new Set()
            const flatC2 = new Set()

            const ingestFlat = (rawList, side) => {
                if (rawList == null || rawList === '') return
                const groups = this.parseNeighborEntityGroups(rawList)
                if (groups.length) {
                    groups.forEach((g) => {
                        const t = String(g.neighborType || '').trim() || 'unknown'
                        const d = String(g.domain || '').trim() || 'default'
                        const pairLabel = `${t}~${d}`
                        const ids = (g.ids || []).map(String).filter(Boolean)
                        if (ids.length) ingestPair(pairLabel, ids, side)
                    })
                    return
                }
                const norm = this.normalizePailEntityListField(rawList)
                const all = this.parseEntityList(norm).map(String).filter(Boolean)
                const shown = all.slice(0, capFlat)
                const target = side === 'c1' ? flatC1 : flatC2
                shown.forEach((id) => target.add(id))
                const overflow = all.length - shown.length
                if (overflow > 0) {
                    const hidden = all.slice(capFlat)
                    const preview = hidden.slice(0, CURATION_BUNDLE_ID_PREVIEW)
                    const bid = `${hubId}|pv|bundle|${side}`
                    addNode(bid, 'entity_bundle', `+${overflow}`, {
                        entityLegendKey: 'entity_bundle',
                        bundleTotal: all.length,
                        bundleDisplayed: shown.length,
                        bundleOverflow: overflow,
                        bundleHiddenPreview: preview,
                        bundleHiddenAllIds: hidden,
                        bundleHiddenNotListed: Math.max(0, hidden.length - preview.length),
                        bundleLinkKind: side === 'c1' ? 'c1' : 'c2',
                        mergePreviewNew: side === 'c2',
                    })
                    pushEdge(hubId, bid, side === 'c1' ? 'c1' : 'c2')
                }
            }

            rows.forEach((row) => {
                const raw1 = row.entity_list ?? row.entity_list1
                const raw2 = row.entity_list2
                ingestFlat(raw1, 'c1')
                if (raw2 != null && raw2 !== '') ingestFlat(raw2, 'c2')
            })

            let pairIdx = 0
            pairAccum.forEach((o, pairLabel) => {
                const entityIds = [...o.ids]
                if (!entityIds.length) return
                const mergePreviewNew = o.inC2 && !o.inC1
                const nid = `${hubId}|pv|td|${pairIdx++}`
                addNode(nid, 'entity_group', pairLabel, {
                    neighborType: pairLabel,
                    entitySubgroups: [{ neighborDomain: '', entityIds }],
                    entityIds,
                    entityLegendKey: `entity:${pairLabel}`,
                    mergeGroupDimension: 'pair',
                    idCountInNode: entityIds.length,
                    mergePreviewNew,
                })
                pushEdge(hubId, nid, 'c1')
            })

            const flatAll = new Set([...flatC1, ...flatC2])
            let flatIdx = 0
            flatAll.forEach((eid) => {
                const in1 = flatC1.has(eid)
                const in2 = flatC2.has(eid)
                const mergePreviewNew = in2 && !in1
                const nid = `${hubId}|pv|flat|${flatIdx++}`
                addNode(nid, 'entity', eid, {
                    entityLegendKey: 'entity',
                    idCountInNode: 1,
                    mergePreviewNew,
                    inBothClusters: in1 && in2,
                })
                pushEdge(hubId, nid, 'c1')
            })

            if (nodes.length <= 1) return null
            const deg = new Map()
            for (let ei = 0; ei < edges.length; ei++) {
                const e = edges[ei]
                deg.set(e.source, (deg.get(e.source) || 0) + 1)
                deg.set(e.target, (deg.get(e.target) || 0) + 1)
            }
            for (let ni = 0; ni < nodes.length; ni++) {
                const n = nodes[ni]
                if (n.type === 'cluster') {
                    n.hubConnectionCount = deg.get(n.id) || 0
                } else if (n.type === 'entity' && n.idCountInNode == null) {
                    n.idCountInNode = 1
                } else if (n.type === 'entity_group' && n.idCountInNode == null && n.entityIds) {
                    n.idCountInNode = n.entityIds.length
                }
            }
            const nodeByIdP = new Map(nodes.map(n => [n.id, n]))
            for (let ei = 0; ei < edges.length; ei++) {
                const e = edges[ei]
                const src = nodeByIdP.get(e.source)
                const tgt = nodeByIdP.get(e.target)
                if (src && src.type === 'cluster' && tgt) {
                    src.totalEntityIds = (src.totalEntityIds || 0) + (tgt.idCountInNode || 0)
                }
                if (tgt && tgt.type === 'cluster' && src) {
                    tgt.totalEntityIds = (tgt.totalEntityIds || 0) + (src.idCountInNode || 0)
                }
            }
            return { nodes, edges }
        },
        buildGraphFromDetailRows(rows, flatCap = CURATION_GRAPH_FLAT_ENTITY_CAP) {
            const nodes = []
            const edgeKeys = new Set()
            const edges = []
            const pushEdge = (source, target, linkKind) => {
                const ek = `${source}->${target}`
                if (edgeKeys.has(ek)) return
                edgeKeys.add(ek)
                const e = { source, target }
                if (linkKind) e.linkKind = linkKind
                edges.push(e)
            }
            const addNode = (id, type, key, extras = {}) => {
                if (!id || nodes.some((n) => n.id === id)) return
                nodes.push({
                    id,
                    type,
                    key: key || id,
                    ...extras,
                })
            }
            const hub = (cid) => `cluster:${cid}`
            const capFlat = Math.max(1, Math.floor(Number(flatCap)) || CURATION_GRAPH_FLAT_ENTITY_CAP)
            const attachEntityList = (hubNodeId, rawList, linkKind) => {
                if (rawList == null || rawList === '') return
                const groups = this.parseNeighborEntityGroups(rawList)
                if (groups.length) {
                    const byPair = new Map()
                    groups.forEach((g) => {
                        const t = String(g.neighborType || '').trim() || 'unknown'
                        const d = String(g.domain || '').trim() || 'default'
                        const pairLabel = `${t}~${d}`
                        const ids = (g.ids || []).map(String).filter(Boolean)
                        if (!ids.length) return
                        const prev = byPair.get(pairLabel) || []
                        byPair.set(pairLabel, prev.concat(ids))
                    })
                    let pairIdx = 0
                    byPair.forEach((rawIds, pairLabel) => {
                        const entityIds = [...new Set(rawIds.map(String))].filter(Boolean)
                        if (!entityIds.length) return
                        const nid = `${hubNodeId}|td|${pairIdx++}`
                        addNode(nid, 'entity_group', pairLabel, {
                            neighborType: pairLabel,
                            entitySubgroups: [{ neighborDomain: '', entityIds }],
                            entityIds,
                            entityLegendKey: `entity:${pairLabel}`,
                            mergeGroupDimension: 'pair',
                            idCountInNode: entityIds.length,
                            nodeLinkKind: linkKind,
                        })
                        pushEdge(hubNodeId, nid, linkKind)
                    })
                } else {
                    const norm = this.normalizePailEntityListField(rawList)
                    const all = this.parseEntityList(norm).map(String).filter(Boolean)
                    const cap = capFlat
                    const shown = all.slice(0, cap)
                    shown.forEach((eid) => {
                        const nid = `${hubNodeId}|__flat__|${eid}`
                        addNode(nid, 'entity', eid, { entityLegendKey: 'entity', idCountInNode: 1, nodeLinkKind: linkKind })
                        pushEdge(hubNodeId, nid, linkKind)
                    })
                    const overflow = all.length - shown.length
                    if (overflow > 0) {
                        const hidden = all.slice(cap)
                        const preview = hidden.slice(0, CURATION_BUNDLE_ID_PREVIEW)
                        const bid = `${hubNodeId}|__bundle__|${linkKind}`
                        addNode(bid, 'entity_bundle', `+${overflow}`, {
                            entityLegendKey: 'entity_bundle',
                            bundleTotal: all.length,
                            bundleDisplayed: shown.length,
                            bundleOverflow: overflow,
                            bundleHiddenPreview: preview,
                            /** Full list of ids not drawn as individual nodes (for download / expand-in-panel). */
                            bundleHiddenAllIds: hidden,
                            bundleHiddenNotListed: Math.max(0, hidden.length - preview.length),
                            bundleLinkKind: linkKind,
                        })
                        pushEdge(hubNodeId, bid, linkKind)
                    }
                }
            }
            rows.forEach((row) => {
                const c1 = row.cluster_id1 || row.cluster_id
                const c2 = row.cluster_id2
                if (!c1) return
                const h1 = hub(c1)
                addNode(h1, 'cluster', c1, { clusterIndex: 1, clusterFullId: c1 })
                if (c2) {
                    const h2 = hub(c2)
                    addNode(h2, 'cluster', c2, { clusterIndex: 2, clusterFullId: c2 })
                    attachEntityList(h1, row.entity_list ?? row.entity_list1, 'c1')
                    attachEntityList(h2, row.entity_list2, 'c2')
                } else {
                    attachEntityList(h1, row.entity_list ?? row.entity_list1, 'c1')
                }
            })
            if (!nodes.length) return null
            const deg = new Map()
            for (let ei = 0; ei < edges.length; ei++) {
                const e = edges[ei]
                deg.set(e.source, (deg.get(e.source) || 0) + 1)
                deg.set(e.target, (deg.get(e.target) || 0) + 1)
            }
            for (let ni = 0; ni < nodes.length; ni++) {
                const n = nodes[ni]
                if (n.type === 'cluster') {
                    n.hubConnectionCount = deg.get(n.id) || 0
                } else if (n.type === 'entity' && n.idCountInNode == null) {
                    n.idCountInNode = 1
                } else if (n.type === 'entity_group' && n.idCountInNode == null && n.entityIds) {
                    n.idCountInNode = n.entityIds.length
                }
            }
            // Sum idCountInNode of child nodes for each cluster's totalEntityIds
            const nodeById = new Map(nodes.map(n => [n.id, n]))
            for (let ei = 0; ei < edges.length; ei++) {
                const e = edges[ei]
                const src = nodeById.get(e.source)
                const tgt = nodeById.get(e.target)
                if (src && src.type === 'cluster' && tgt) {
                    src.totalEntityIds = (src.totalEntityIds || 0) + (tgt.idCountInNode || 0)
                }
                if (tgt && tgt.type === 'cluster' && src) {
                    tgt.totalEntityIds = (tgt.totalEntityIds || 0) + (src.idCountInNode || 0)
                }
            }
            return { nodes, edges }
        },
        /**
         * Builds Pail-style bracket neighbor strings from JSON rows (some serializers return arrays of objects).
         */
        neighborJsonArrayToPailBracketString(groups) {
            if (!Array.isArray(groups) || !groups.length) return ''
            const parts = []
            for (let i = 0; i < groups.length; i++) {
                const o = groups[i]
                if (!o || typeof o !== 'object') continue
                const keys = Object.keys(o)
                const typeKey   = keys.find(k => /type/i.test(k))
                const domainKey = keys.find(k => /domain/i.test(k))
                const idsKey    = keys.find(k => /ids?$/i.test(k))
                const t = typeKey   ? String(o[typeKey]   ?? '').trim() : ''
                const d = domainKey ? String(o[domainKey] ?? '').trim() : ''
                let ids = idsKey != null ? o[idsKey] : null
                if (ids == null) continue
                if (!Array.isArray(ids)) {
                    ids = String(ids)
                        .split(/[,|\s]+/)
                        .map((x) => x.trim())
                        .filter(Boolean)
                }
                const idStr = ids.map(String).filter(Boolean).join(', ')
                if (!t || !d || !idStr) continue
                parts.push(`{entity_neighbor_type=${t}, entity_neighbor_domain=${d}, ids=[${idStr}]}`)
            }
            if (!parts.length) return ''
            console.log('Converted neighbor JSON array to Pail bracket string:', parts)
            return `[${parts.join(', ')}]`
        },
        /**
         * Pail returns entity_list* either as bracket-strings (positional data[] rows) or as JSON arrays of structs.
         * Downstream parsing expects bracket-strings like production query results.
         */
        normalizePailEntityListField(raw) {
            if (raw == null || raw === '') return ''
            if (typeof raw === 'string') {
                const s = raw.trim()
                if (!s) return ''
                if (s.startsWith('[')) {
                    try {
                        const j = JSON.parse(s)
                        if (Array.isArray(j) && j.length && typeof j[0] === 'object' && j[0] !== null) {
                            if (Object.keys(j[0]).some(k => /type/i.test(k))) {
                                return this.neighborJsonArrayToPailBracketString(j)
                            }
                        }
                    } catch (_) {
                        /* not JSON — treat as Pail bracket-string or flat list */
                    }
                }
                return s
            }
            if (Array.isArray(raw)) {
                if (!raw.length) return ''
                if (typeof raw[0] === 'object' && raw[0] !== null && !Array.isArray(raw[0])) {
                    return this.neighborJsonArrayToPailBracketString(raw)
                }
                return raw.map(String).join(',')
            }
            if (typeof raw === 'object') {
                return this.neighborJsonArrayToPailBracketString([raw])
            }
            return String(raw).trim()
        },
        /**
         * Parses neighbor-group strings. Two API shapes are supported:
         *  - keyed:      [{entity_neighbor_type=type1, entity_neighbor_domain=domain3, ids=[9f5c209bff2e]}, ...]
         *  - positional: [[type1 domain1 [id1 id2 id3]] [type2 domain2 [id4 id5]]] (no keys, no commas anywhere)
         * entity_list1 / entity_list2 map to cluster hubs separately.
         * Neighbor rows are grouped into one graph node per neighborType~domain pair.
         * Each node uses neighborType as that combined label and entitySubgroups / entityIds for ids.
         */
        parseNeighborEntityGroups(raw) {
            const s = String(this.normalizePailEntityListField(raw) ?? '').trim()
            if (!s) return []
            if (/entity_(?:neighbor_)?type\s*=/i.test(s)) {
                return this.parseKeyedEntityGroups(s)
            }
            if (/\[\s*[^\s[\]]+\s+[^\s[\]]+\s+\[/.test(s)) {
                return this.parsePositionalEntityGroups(s)
            }
            return []
        },
        /** Keyed shape: {entity_type=.., entity_domain=.., ids=[..]} blocks, comma-separated. */
        parseKeyedEntityGroups(s) {
            let body = s
            if (body.startsWith('[')) body = body.slice(1).trim()
            if (body.endsWith(']')) body = body.slice(0, -1).trim()
            if (!body) return []
            const segments = body.split(/\}\s*,\s*\{/)
            const out = []
            for (let i = 0; i < segments.length; i++) {
                let block = segments[i].trim()
                if (!block.startsWith('{')) block = `{${block}`
                if (!block.endsWith('}')) block = `${block}}`
                // match both API shapes: entity_type / entity_neighbor_type, entity_domain / entity_neighbor_domain
                const typeM = block.match(/entity_(?:neighbor_)?type\s*=\s*([^,\s}]+)/)
                const domainM = block.match(/entity_(?:neighbor_)?domain\s*=\s*([^,\s}]+)/)
                const idsM = block.match(/ids\s*=\s*\[([^\]]*)\]/i)
                if (!typeM || !domainM || !idsM) continue
                const neighborType = String(typeM[1]).trim()
                const domain = String(domainM[1]).trim()
                const ids = String(idsM[1])
                    .split(/[,|\s]+/)
                    .map((x) => x.trim())
                    .filter(Boolean)
                if (ids.length) out.push({ neighborType, domain, ids })
            }
            return out
        },
        /**
         * Positional shape: [[type1 domain1 [id1 id2 ...]] [type2 domain2 [id3 ...]]] — no keys,
         * blocks and ids separated by whitespace only (no commas anywhere in the string).
         */
        parsePositionalEntityGroups(s) {
            const blockRe = /\[\s*([^\s[\]]+)\s+([^\s[\]]+)\s+\[([^\]]*)\]\s*\]/g
            const out = []
            let m
            while ((m = blockRe.exec(s)) !== null) {
                const neighborType = m[1].trim()
                const domain = m[2].trim()
                const ids = m[3]
                    .split(/\s+/)
                    .map((x) => x.trim())
                    .filter(Boolean)
                if (ids.length) out.push({ neighborType, domain, ids })
            }
            return out
        },
        parseEntityList(raw) {
            if (raw == null || raw === '') return []
            if (Array.isArray(raw)) {
                return raw.flatMap((x) => this.parseEntityList(x))
            }
            if (typeof raw === 'object') {
                const vals = Object.values(raw)
                if (vals.length && vals.every((v) => typeof v === 'string' || typeof v === 'number')) {
                    return vals.map(String)
                }
            }
            const s = String(raw).trim()
            if (!s) return []
            if (s.startsWith('[')) {
                try {
                    const j = JSON.parse(s)
                    return Array.isArray(j) ? j.map(String) : [String(j)]
                } catch (_) {
                    /* fall through */
                }
            }
            return s.split(/[\s,|]+/).filter(Boolean)
        },
        rowsFromResult(result) {
            if (!result?.column_info || !result?.data) return []
            return result.data.map((row) => {
                const o = {}
                if (Array.isArray(row)) {
                    result.column_info.forEach((c, i) => {
                        o[c.name] = row[i]
                    })
                } else if (row && typeof row === 'object') {
                    result.column_info.forEach((c) => {
                        o[c.name] = row[c.name]
                    })
                }
                return o
            })
        },
        sleep(ms) {
            return new Promise((r) => setTimeout(r, ms))
        },
        async runPailQuery(query) {
            const cid = this.cid
            if (!cid) throw new Error('No client selected.')
            console.log('Running Pail query:', this.$store.state.pailUrl)
            const base = `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${cid}`
            const headers = {
                Authorization: `Bearer ${this.$store.state.accessToken}`,
                'Content-Type': 'application/json',
            }
            const post = await axios.post(`${base}/query_executions`, { query }, { headers })
            const qid = post.data?.query_execution_id
            if (!qid) throw new Error('No query_execution_id returned from Pail.')

            await this.sleep(800)

            for (let i = 0; i < MAX_POLL; i++) {
                const statusRes = await axios.get(`${base}/query_status/${qid}`, {
                    headers: { Authorization: `Bearer ${this.$store.state.accessToken}` },
                })
                const st = statusRes.data
                if (st.State === 'FAILED') {
                    throw new Error(st.StateChangeReason || 'Query failed.')
                }
                if (st.State && st.State !== 'QUEUED' && st.State !== 'RUNNING') {
                    const res = await axios.get(`${base}/query_executions/${qid}`, {
                        headers: { Authorization: `Bearer ${this.$store.state.accessToken}` },
                    })
                    return res.data
                }
                await this.sleep(POLL_MS)
            }
            throw new Error('Query timed out.')
        },
        recommendationIdForPayload(row, idx) {
            if (row.recommendation_id != null && String(row.recommendation_id).trim() !== '') {
                return String(row.recommendation_id)
            }
            if (this.dataset === 'split') {
                return `${this.splitRowClusterId(row)}|${idx}`
            }
            return `${row.cluster_id1}|${row.cluster_id2}|${idx}`
        },
        /**
         * Split truthset: entity_lists from neighbor-group strings or flat entity_list (see API contract).
         * Uses cached detail rows when present (entity_list from detail query).
         */
        buildSplitEntityListsForRow(row) {
            const rid =
                row.recommendation_id != null && String(row.recommendation_id).trim() !== ''
                    ? String(row.recommendation_id).trim()
                    : null
            const clusterId = String(row.cluster_id ?? row.cluster_id1 ?? '').trim()
            if (!rid && !clusterId) return []
            const cacheKey = rid ? this.cacheKeyForRecommendation(rid) : `${this.dataset}::${clusterId}`
            const entry = this.detailCache[cacheKey]
            let raw = null
            if (entry && entry.rows && entry.rows.length) {
                raw = entry.rows[0].entity_list
            }
            if (raw == null && row.entity_list != null) raw = row.entity_list
            if (raw == null || raw === '') return []

            const groups = this.parseNeighborEntityGroups(raw)
            if (groups.length) {
                const merged = new Map()
                groups.forEach((g) => {
                    const t = String(g.neighborType || '').trim() || 'unknown'
                    const dom = String(g.domain || '').trim() || 'default'
                    const k = `${t}\0${dom}`
                    const cur = merged.get(k) || []
                    merged.set(k, cur.concat((g.ids || []).map(String).filter(Boolean)))
                })
                return Array.from(merged.entries())
                    .map(([k, ids]) => {
                        const [entity_type, entity_domain] = k.split('\0')
                        const deduped = [...new Set(ids)].filter(Boolean)
                        return { entity_type, entity_domain, ids: deduped }
                    })
                    .filter((e) => e.ids.length)
            }
            const norm = this.normalizePailEntityListField(raw)
            if (!norm) return []
            const flat = this.parseEntityList(norm).map(String).filter(Boolean)
            if (!flat.length) return []
            return [{ entity_type: 'entity', entity_domain: 'default', ids: flat }]
        },
        buildSubmitPayload() {
            const curations = []
            this.recommendations.forEach((row, idx) => {
                const key = this.rowKey(row, idx)
                const decision = this.decisions[key]
                if (!decision) return

                if (this.dataset === 'split') {
                    const cluster_id = String(row.cluster_id ?? row.cluster_id1 ?? '').trim()
                    if (decision === 'reject') {
                        curations.push({
                            cluster_id,
                            decision: 'reject',
                            // operation: 'reject',
                            operation: 'physical'
                        })
                        return
                    }
                    let split_count = Number(row.split_into)
                    if (!Number.isFinite(split_count) || split_count < 2) split_count = DEFAULT_SPLIT_INTO
                    split_count = Math.min(99, Math.floor(split_count))
                    const entity_lists = this.buildSplitEntityListsForRow(row)
                    curations.push({
                        cluster_id,
                        decision: 'accept',
                        // operation: 'split',
                        operation: 'physical',
                        split_count,
                        entity_lists,
                    })
                    return
                }

                const isReject = decision === 'reject'
                curations.push({
                    recommendation_id: this.recommendationIdForPayload(row, idx),
                    source_cluster: String(row.cluster_id1 ?? ''),
                    target_cluster: String(row.cluster_id2 ?? ''),
                    decision: isReject ? 'reject' : 'accept',
                    // operation: isReject ? 'reject' : 'merge',
                    operation: 'physical',
                })
            })
            return { curations }
        },
        async submitDecisions() {
            if (!this.allDecisionsComplete || !this.cid) return
            this.submitting = true
            const body = this.buildSubmitPayload()
            const nextOffset = this.computeNextCurationOffsetAfterSubmit()
            const url = `${this.$store.state.graphServiceUrl}/v1/cids/${encodeURIComponent(this.cid)}/curation/truthset?dataset=${this.dataset}`
            try {
                await axios.post(url, body, {
                    headers: {
                        Authorization: `Bearer ${this.$store.state.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                })
                this.$store.dispatch('addToast', {
                    type: 'success',
                    message: 'Decisions saved successfully.',
                })
                this.decisions = Object.create(null)
                if (nextOffset != null) {
                    try {
                        await this.setCurationOffset(nextOffset)
                        await this.refreshOffset()
                        await this.loadRecommendations({ skipOffsetRefresh: true })
                    } catch (offErr) {
                        const offMsg =
                            offErr.response?.data?.message ||
                            offErr.response?.data?.error ||
                            offErr.message ||
                            'Unknown error'
                        this.$store.dispatch('addToast', {
                            type: 'warning',
                            message: `Decisions were saved, but updating the offset or reloading the list failed (${offMsg}).`,
                            config: { timeout: 6000 },
                        })
                    }
                }
            } catch (e) {
                const status = e.response?.status
                const msg =
                    e.response?.data?.message ||
                    e.response?.data?.error ||
                    e.message ||
                    'Submit failed.'
                if (status === 404 || status === 405) {
                    this.$store.dispatch('addToast', {
                        type: 'warning',
                        message:
                            'Something went wrong while submitting the curation decisions. Please try again.',
                        config: { timeout: 5000 },
                    })
                } else {
                    this.$store.dispatch('addToast', { type: 'error', message: msg })
                }
            } finally {
                this.submitting = false
            }
        },
    },
}
</script>

<style scoped>
.graph-curation-page {
    --curation-list-rows: 8;
    --curation-table-row-h: 2.75rem;
    --curation-table-head-h: 2.5rem;
    --curation-table-max: calc(var(--curation-table-head-h) + var(--curation-list-rows) * var(--curation-table-row-h));
}

/* ── Bench row: fill remaining viewport ─────────────────────────────────── */
@media (min-width: 992px) {
    .curation-bench-row {
        height: calc(100vh - 14rem);
        min-height: 520px;
    }
    .curation-bench-row > .curation-bench-col {
        height: 100%;
        min-height: 0;
    }
    .curation-bench-row .recommendations-card,
    .curation-bench-row .graph-detail-card {
        height: 100%;
        min-height: 0;
    }
}

@media (max-width: 991.98px) {
    .curation-bench-row .recommendations-card {
        min-height: 420px;
    }
    .curation-bench-row .graph-detail-card {
        min-height: 480px;
    }
}

.curation-bench-row > .curation-bench-col {
    min-height: 0;
}


.curation-list-body {
    min-height: 0;
    min-width: 0;
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    padding: 0.75rem 0.875rem 0.75rem 0.75rem !important;
}

.curation-hero {
    border-left: 4px solid #0d6efd;
    padding-left: 1rem;
    max-width: 52rem;
}

.recommendations-card-header {
    padding: 0.875rem 1.1rem 0.875rem 1.1rem;
    margin-bottom: 0;
    flex-shrink: 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.graph-detail-card-header {
    padding: 0.875rem 1.1rem 0.875rem 1.1rem;
    margin-bottom: 0;
    flex-shrink: 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

/* ── Unified topbar ──────────────────────────────────────────────────── */
.curation-topbar-card {
    margin-bottom: 1rem;
    background: #ffffff;
    border-left: 4px solid #0d6efd !important;
}

.curation-topbar-body {
    padding: 0.875rem 1.25rem;
}

.curation-topbar-inner {
    display: flex;
    align-items: center;
    gap: 0.5rem 0.75rem;
    flex-wrap: wrap;
    min-width: 0;
}

.curation-controls-group {
    display: flex;
    align-items: center;
    gap: 0.5rem 0.75rem;
    flex: 1 1 auto;
    min-width: 0;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.curation-topbar-sep {
    width: 1px;
    height: 2.25rem;
    background: rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
    align-self: center;
    display: none;
}

@media (min-width: 1200px) {
    .curation-topbar-sep { display: block; }
}

.curation-stats-group {
    display: flex;
    align-items: center;
    gap: 0.5rem 0.75rem;
    flex-shrink: 0;
    flex-wrap: nowrap;
}

@media (max-width: 1199.98px) {
    .curation-topbar-inner { row-gap: 0.5rem; }
    .curation-controls-group { width: 100%; }
}

/* Inline field label */
.curation-inline-label {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6c757d;
    white-space: nowrap;
    flex-shrink: 0;
}

/* Vertical separator between toolbar groups */
.curation-v-sep {
    width: 1px;
    height: 1.75rem;
    background: rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
    align-self: center;
}

/* Records number input */
.curation-records-input {
    width: 5rem;
    min-width: 3.5rem;
    max-width: 5.5rem;
}

.graph-detail-card {
    overflow: hidden;
}

.cluster-detail-panel {
    flex-shrink: 0;
    padding: 0.85rem 1rem 1rem;
    background: #f5f8fb;
    border-top: 2px solid #e3ebf6;
}

.cluster-detail-panel-empty {
    flex-shrink: 0;
    padding: 1.45rem 1.45rem 1.5rem;
    background: #f5f8fb;
    border-top: 2px solid #e3ebf6;
}

.cluster-detail-title {
    margin: 0 0 0.5rem;
    padding: 0;
    line-height: 1.35;
    font-size: 1.05rem;
    font-weight: 600;
    color: #212529;
}

.cluster-detail-summary-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 0.75rem;
    margin-bottom: 0.6rem;
}

.cluster-pair-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem 1rem;
    flex: 1 1 auto;
    min-width: min(100%, 10rem);
    padding: 0.45rem 0.65rem;
    margin-bottom: 0;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 0.375rem;
    color: #212529;
    line-height: 1.45;
}

.cluster-detail-summary-row .cluster-pair-bar {
    flex: 1 1 12rem;
    min-width: 0;
}

.cluster-pair-ids {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem 0.65rem;
    min-width: 0;
}

.cluster-pair-id {
    word-break: break-word;
    overflow-wrap: anywhere;
}

.cluster-pair-sep {
    flex-shrink: 0;
    opacity: 0.55;
    font-weight: 500;
}

.cluster-section-label {
    display: block;
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #6c757d;
    margin-bottom: 0.4rem;
}

.cluster-section-label--inline {
    display: inline;
    margin-bottom: 0;
    margin-right: 0.35rem;
    line-height: 1.2;
    vertical-align: middle;
}

.cluster-anchor-row {
    margin-bottom: 1rem;
}

.cluster-anchor-row--inline {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem 0.4rem;
    margin-bottom: 0;
    flex: 0 1 auto;
}

.cluster-anchor-row--header {
    flex-shrink: 0;
    margin-bottom: 0;
}

.cluster-anchor-row .btn-anchor {
    background-image: none;
    font-weight: 500;
    line-height: 1.3;
    transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
}

.cluster-anchor-row .btn-anchor-idle {
    color: #495057;
    background-color: #fff;
    border-color: #ced4da;
}

.cluster-anchor-row .btn-anchor-idle:hover {
    background-color: #f0f4ff;
    border-color: #93a8cc;
    color: #212529;
}

/* Both active = blue */
.cluster-anchor-row .btn-anchor-active {
    color: #fff;
    background-color: #0d6efd;
    border-color: #0d6efd;
    background-image: none;
}
.cluster-anchor-row .btn-anchor-active:hover {
    background-color: #0b5ed7;
    border-color: #0a58ca;
    color: #fff;
}

/* C1 active = blue */
.cluster-anchor-row .btn-anchor-c1.btn-anchor-active {
    background-color: #0d6efd;
    border-color: #0d6efd;
}
.cluster-anchor-row .btn-anchor-c1.btn-anchor-active:hover {
    background-color: #0b5ed7;
    border-color: #0a58ca;
}

/* C2 active = teal */
.cluster-anchor-row .btn-anchor-c2.btn-anchor-active {
    background-color: #0ca678;
    border-color: #0ca678;
}
.cluster-anchor-row .btn-anchor-c2.btn-anchor-active:hover {
    background-color: #0b9268;
    border-color: #0a8460;
}

.cluster-anchor-row .btn-anchor:focus {
    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.28);
}

.cluster-query-block {
    margin: 0;
}

.cluster-detail-panel .cluster-query-snippet {
    max-height: min(calc(13rem * 1.45), calc(44vh * 1.45));
    overflow-y: auto;
}

.cluster-query-snippet {
    display: block;
    font-size: 0.75rem;
    line-height: 1.5;
    padding: 0.65rem 0.85rem;
    margin: 0;
    color: #212529;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 0.375rem;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
}

/* Wrap SQL to width so the block stays readable without horizontal scroll */
.cluster-query-snippet--plain {
    white-space: normal;
    overflow-wrap: anywhere;
}

.graph-detail-card-body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #fff;
    padding: 0.875rem 1rem 0.875rem 1rem !important;
}

.lead-small {
    font-size: 0.95rem;
}


/* Selected row: left accent + tinted background */
.curation-data-table tbody tr.table-active > td {
    background-color: #ebf2ff !important;
}
.curation-data-table tbody tr.table-active > td:first-child {
    box-shadow: inset 3px 0 0 #0d6efd;
}

.dataset-toggle .btn {
    min-width: 4.5rem;
}

.curation-data-table thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06);
}

.curation-th-checkbox,
.curation-td-checkbox {
    width: 2.35rem;
    padding-left: 0.35rem;
    padding-right: 0.35rem;
    vertical-align: middle;
}

.curation-bulk-bar {
    flex-shrink: 0;
    min-width: 0;
}

.cluster-link-btn {
    display: inline;
    max-width: 100%;
    padding: 0;
    margin: 0;
    border: none;
    background: none;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
    line-height: inherit;
    text-decoration: none;
    word-break: break-word;
}

.cluster-link-btn:hover {
    color: #0d6efd;
    text-decoration: underline;
}

.cluster-link-btn.is-anchor-active {
    color: #0d6efd;
    font-weight: 600;
    text-decoration: none;
}

.cluster-link-btn.is-anchor-active:hover {
    text-decoration: underline;
}


/* Inline stat chip */
.curation-stat-chip {
    display: flex;
    flex-direction: column;
    gap: 0.08rem;
    min-width: 0;
    padding: 0.3rem 0.7rem;
    border-radius: 0.5rem;
    background: rgba(0, 0, 0, 0.035);
    border: 1px solid rgba(0, 0, 0, 0.055);
}

.curation-stat-chip__label {
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #8898aa;
    white-space: nowrap;
    line-height: 1.1;
}

.curation-stat-chip__value {
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.15;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
    white-space: nowrap;
}

.curation-stat-chip__value--decisions {
    color: #212529;
}

.graph-curation-page .btn-submit-review {
    background-image: none;
    background-color: #198754;
    border-color: #198754;
    color: #fff;
}

.graph-curation-page .btn-submit-review:hover:not(:disabled) {
    background-image: none;
    background-color: #157347;
    border-color: #146c43;
    color: #fff;
}

.graph-curation-page .btn-submit-review:focus {
    box-shadow: 0 0 0 0.2rem rgba(25, 135, 84, 0.35);
}

.curation-field {
    min-width: 8.5rem;
}

.curation-table-wrap {
    flex: 0 1 auto;
    max-height: var(--curation-table-max);
    min-height: 0;
    min-width: 0;
    overflow-x: hidden;
    overflow-y: auto;
    border-radius: 0.375rem;
    border: 1px solid rgba(0, 0, 0, 0.06);
    -webkit-overflow-scrolling: touch;
}

/* At desktop the bench is viewport-height; let the table grow to fill the card */
@media (min-width: 992px) {
    .curation-table-wrap {
        flex: 1 1 0;
        max-height: none;
    }
}

.curation-data-table {
    table-layout: fixed;
    width: 100%;
}

.curation-data-table .curation-col-w-check {
    width: 2.35rem;
}

.curation-data-table .curation-col-w-idx {
    width: 2.25rem;
}

.curation-data-table .curation-col-w-recid {
    width: 4rem;
}

.curation-data-table .curation-col-w-actions {
    width: 5.25rem;
}

.curation-data-table .curation-col-w-splitinto {
    width: 4.75rem;
}

.curation-col-splitinto {
    vertical-align: middle;
}

.curation-splitinto-input {
    min-width: 0;
    max-width: 100%;
}

.curation-data-table .curation-col-cluster,
.curation-data-table .curation-col-splitinto,
.curation-data-table .curation-col-actions,
.curation-data-table .curation-col-actions-h {
    vertical-align: middle;
}

.curation-data-table .curation-col-cluster {
    overflow: hidden;
    max-width: 0;
}

.curation-col-actions {
    padding-left: 0.35rem;
    padding-right: 0.35rem;
}

.curation-action-btns .curation-decision-btn {
    width: 2.1rem;
    height: 1.8rem;
    padding: 0;
    font-size: 0.8rem;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    line-height: 1;
    transition: transform 0.1s, box-shadow 0.1s;
}
.curation-action-btns .curation-decision-btn:hover {
    transform: scale(1.08);
    box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}

.cluster-link-btn--cell {
    display: block;
    width: 100%;
    max-width: 100%;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.curation-row {
    cursor: pointer;
}

/* Cluster ID sub-label inside anchor buttons */
.cluster-btn-id {
    display: block;
    font-size: 9px;
    opacity: 0.72;
    letter-spacing: 0.01em;
    max-width: 11rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1;
    margin-top: 1px;
}

.graph-detail-card .graph-detail-inner {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.graph-detail-card :deep(.cgraph-wrap) {
    flex: 1 1 0;
    min-height: 0 !important;
    display: flex;
    flex-direction: column;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 0.375rem;
    overflow: hidden;
}

/* Let the graph fill all remaining space in the card — no fixed min-height */
.graph-detail-card :deep(.cgraph-canvas) {
    flex: 1 1 0 !important;
    min-height: 0 !important;
    max-height: none;
}

@media (max-width: 991.98px) {
    .graph-detail-card :deep(.cgraph-canvas) {
        min-height: 360px !important;
    }
}

body.dark-mode .graph-detail-card :deep(.cgraph-wrap) {
    border-color: rgba(255, 255, 255, 0.1);
}

.selected-count{
    padding-left: 10px;
}
.font-monospace {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
}

body.dark-mode .curation-hero,

body.dark-mode .curation-topbar-card {
    background: #1a1d23;
}

body.dark-mode .curation-topbar-sep {
    background: rgba(255, 255, 255, 0.12);
}

body.dark-mode .curation-inline-label {
    color: #adb5bd;
}

body.dark-mode .curation-v-sep {
    background: rgba(255, 255, 255, 0.12);
}

body.dark-mode .curation-stat-chip__label {
    color: #adb5bd;
}

body.dark-mode .curation-stat-chip {
    background: rgba(255, 255, 255, 0.05);
}

body.dark-mode .curation-stat-chip__value--decisions {
    color: #e9ecef;
}

body.dark-mode .curation-stat-chip__value.text-primary {
    color: #6ea8fe !important;
}

body.dark-mode .graph-curation-page .btn-submit-review {
    background-image: none;
    background-color: #198754;
    border-color: #198754;
}

body.dark-mode .graph-curation-page .btn-submit-review:hover:not(:disabled) {
    background-image: none;
    background-color: #157347;
    border-color: #146c43;
}

body.dark-mode .cluster-link-btn:hover,
body.dark-mode .cluster-link-btn.is-anchor-active {
    color: #6ea8fe;
}

.curation-range-value {
    color: #212529;
    letter-spacing: 0.01em;
}

body.dark-mode .curation-range-value {
    color: #e9ecef;
}

/* Graph ID filter input */
.cluster-graph-id-filter {
    padding: 0.5rem 0;
}

.curation-graph-id-filter-input {
    max-width: 18rem;
}

body.dark-mode .curation-table-wrap {
    border-color: rgba(255, 255, 255, 0.08);
}

body.dark-mode .cluster-detail-panel,
body.dark-mode .cluster-detail-panel-empty {
    background: #20242c;
    border-top-color: rgba(255, 255, 255, 0.12);
}

body.dark-mode .cluster-detail-title {
    color: #e9ecef;
}

body.dark-mode .cluster-pair-bar,
body.dark-mode .cluster-query-snippet {
    background: #1e2128;
    border-color: rgba(255, 255, 255, 0.12);
    color: #e9ecef;
}

body.dark-mode .cluster-section-label {
    color: #adb5bd;
}

body.dark-mode .cluster-anchor-row .btn-anchor-idle {
    color: #dee2e6;
    background-color: #1e2128;
    border-color: rgba(255, 255, 255, 0.2);
}

body.dark-mode .cluster-anchor-row .btn-anchor-idle:hover {
    background-color: #2c313a;
    border-color: rgba(255, 255, 255, 0.28);
    color: #fff;
}

body.dark-mode .graph-detail-card-body {
    background: #1a1d23;
    padding: 0.875rem 1rem 0.875rem 1rem;
}

body.dark-mode .curation-data-table tbody tr.table-active > td {
    background-color: #1a2540 !important;
}

body.dark-mode .cluster-anchor-row .btn-anchor-c2.btn-anchor-active {
    background-color: #0a8a63;
    border-color: #0a8a63;
}

body.dark-mode .curation-table-wrap,
body.dark-mode .curation-data-table {
    --bs-table-hover-bg: rgba(255,255,255,0.04);
}
</style>
